import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Cloud, Monitor, Key, Cpu, DollarSign, Users, AlertTriangle,
  Calendar, TrendingUp, TrendingDown, Building, RefreshCw,
  Package, CreditCard, Zap, ArrowRight, BarChart3, PieChart
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  Treemap
} from 'recharts';
import { toast } from 'sonner';
import CloudCostDashboard from '../components/CloudCostDashboard';
import { useAWSCredentials } from '../components/AWSConnect';
import { useAzureCredentials } from '../components/AzureConnect';
import { useGCPCredentials } from '../components/GCPConnect';
import { useDemoData } from '../components/useDemoData';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
const CATEGORY_COLORS = {
  'Cloud Infrastructure': '#0EA5E9',
  'SaaS Applications': '#8B5CF6', 
  'Software Licenses': '#F59E0B',
  'AI/ML Services': '#E11D48'
};

const getDemoTechSpendData = () => ({
  overview: {
    total_spend: 428600,
    categories: [
      { name: 'Cloud Infrastructure', amount: 284500, percent: 66.4, change: -5.2, items: [
        { vendor: 'AWS', amount: 178400, change: -3.8 },
        { vendor: 'Azure', amount: 68200, change: -8.1 },
        { vendor: 'GCP', amount: 37900, change: -4.2 },
      ]},
      { name: 'SaaS Applications', amount: 82400, percent: 19.2, change: 12.3, items: [
        { vendor: 'Salesforce', amount: 28500, change: 5.0 },
        { vendor: 'Slack', amount: 18200, change: 8.0 },
        { vendor: 'GitHub Enterprise', amount: 14800, change: 15.0 },
        { vendor: 'Datadog', amount: 20900, change: 22.0 },
      ]},
      { name: 'Software Licenses', amount: 38500, percent: 9.0, change: -2.1, items: [
        { vendor: 'Microsoft 365', amount: 22400, change: 0 },
        { vendor: 'JetBrains', amount: 8600, change: -5.0 },
        { vendor: 'Other', amount: 7500, change: -3.0 },
      ]},
      { name: 'AI/ML Services', amount: 23200, percent: 5.4, change: 45.8, items: [
        { vendor: 'OpenAI', amount: 12800, change: 65.0 },
        { vendor: 'AWS SageMaker', amount: 10400, change: 28.0 },
      ]},
    ],
    trend: [
      { month: 'Oct', cloud: 312000, saas: 74200, licenses: 39800, aiml: 15800 },
      { month: 'Nov', cloud: 305000, saas: 76800, licenses: 39200, aiml: 17400 },
      { month: 'Dec', cloud: 298000, saas: 78500, licenses: 38900, aiml: 19200 },
      { month: 'Jan', cloud: 292000, saas: 79800, licenses: 38700, aiml: 20800 },
      { month: 'Feb', cloud: 288000, saas: 81200, licenses: 38600, aiml: 22100 },
      { month: 'Mar', cloud: 284500, saas: 82400, licenses: 38500, aiml: 23200 },
    ]
  },
  vendors: {
    total_vendors: 14,
    under_review: 3,
    contracts_expiring_soon: 2,
    potential_consolidation_savings: 18400,
  }
});

const TechSpend = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Cloud provider connection status
  const { isConnected: awsConnected } = useAWSCredentials();
  const { isConnected: azureConnected } = useAzureCredentials();
  const { isConnected: gcpConnected } = useGCPCredentials();
  const hasCloudConnection = awsConnected || azureConnected || gcpConnected;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, vendorRes] = await Promise.all([
        axios.get(`${API_URL}/tech-spend/overview`),
        axios.get(`${API_URL}/tech-spend/vendor-analysis`)
      ]);
      setData(overviewRes.data);
      setVendorData(vendorRes.data);
    } catch (error) {
      console.error('Failed to fetch tech spend data:', error);
      if (shouldShowData) {
        const demo = getDemoTechSpendData();
        setData(demo.overview);
        setVendorData(demo.vendors);
      } else {
        toast.error('Failed to load technology spend data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="tech-spend-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-24" /></CardContent></Card>
          ))}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-80" /></CardContent></Card>
      </div>
    );
  }

  const categoryBreakdown = data?.category_breakdown || {};
  const saasDetails = data?.saas_details || {};
  const licenseDetails = data?.license_details || {};
  const aiDetails = data?.ai_details || {};
  const cloudDetails = data?.cloud_details || {};
  const kpis = data?.kpis || {};
  const upcomingRenewals = data?.upcoming_renewals || [];
  const optimizations = data?.optimization_opportunities || [];

  // Prepare chart data
  const categoryChartData = Object.entries(categoryBreakdown).map(([name, value]) => ({
    name: name.replace('Infrastructure', 'Infra').replace('Applications', 'Apps'),
    value: Math.round(value),
    fullName: name,
    fill: CATEGORY_COLORS[name] || '#64748b'
  }));

  const saasSubscriptions = saasDetails.subscriptions || [];
  const licenses = licenseDetails.licenses || [];
  const aiServices = aiDetails.services || [];

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="tech-spend">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <Monitor className="w-7 h-7 text-emerald-400" />
            Total Tech Spend <span className="text-emerald-400">Command Center</span>
          </h1>
          <p className="text-slate-700 text-sm mt-1">
            Complete visibility: Cloud + SaaS + Licenses + AI/ML
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2 border-emerald-500/40 text-emerald-300 hover:border-emerald-500 hover:bg-emerald-500/10"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Live Cloud Costs - Shows when providers are connected */}
      {hasCloudConnection && (
        <div className="mb-6">
          <CloudCostDashboard />
        </div>
      )}

      {/* Total Spend Banner */}
      <Card className="bg-slate-900 text-slate-100 border-emerald-500/30" data-testid="total-spend-banner">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Total Monthly Technology Spend</p>
              <p className="text-4xl font-bold text-emerald-400 mt-1 tabular-nums">
                {formatCurrency(data?.total_tech_spend || 0)}
              </p>
              <p className="text-slate-300 text-sm mt-2">
                Across <span className="text-white font-semibold">{saasSubscriptions.length}</span> SaaS apps, <span className="text-white font-semibold">{licenses.length}</span> licenses, <span className="text-white font-semibold">{aiServices.length}</span> AI services
              </p>
            </div>
            <div className="flex gap-4">
              <div className="bg-slate-950 border border-emerald-500/20 rounded-lg p-4">
                <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Per Employee</p>
                <p className="text-2xl font-bold text-white tabular-nums">{formatCurrency(kpis.spend_per_employee || 0)}</p>
              </div>
              <div className="bg-slate-950 border border-emerald-500/20 rounded-lg p-4">
                <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">SaaS Utilization</p>
                <p className="text-2xl font-bold text-white tabular-nums">{kpis.saas_utilization || 0}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-sky-500" data-testid="cloud-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <Cloud className="w-8 h-8 text-sky-500" />
              {cloudDetails.trend >= 0 ? (
                <Badge className="bg-rose-100 text-rose-700">+{cloudDetails.trend}%</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700">{cloudDetails.trend}%</Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 dark:text-slate-400 mt-3">Cloud Infrastructure</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(categoryBreakdown['Cloud Infrastructure'] || 0)}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-cyan-500" data-testid="saas-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <Package className="w-8 h-8 text-emerald-500" />
              <Badge className="bg-emerald-100 text-emerald-700">{saasSubscriptions.length} apps</Badge>
            </div>
            <p className="text-sm text-slate-400 dark:text-slate-400 mt-3">SaaS Applications</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(categoryBreakdown['SaaS Applications'] || 0)}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="license-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <Key className="w-8 h-8 text-amber-500" />
              <Badge className="bg-amber-100 text-amber-700">{licenses.length} licenses</Badge>
            </div>
            <p className="text-sm text-slate-400 dark:text-slate-400 mt-3">Software Licenses</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(categoryBreakdown['Software Licenses'] || 0)}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500" data-testid="ai-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <Zap className="w-8 h-8 text-rose-500" />
              {aiDetails.trend >= 0 ? (
                <Badge className="bg-rose-100 text-rose-700">+{aiDetails.trend}%</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700">{aiDetails.trend}%</Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 dark:text-slate-400 mt-3">AI/ML Services</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(categoryBreakdown['AI/ML Services'] || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="overview" className="gap-2" data-testid="overview-tab">
            <PieChart className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="saas" className="gap-2" data-testid="saas-tab">
            <Package className="w-4 h-4" />
            SaaS ({saasSubscriptions.length})
          </TabsTrigger>
          <TabsTrigger value="licenses" className="gap-2" data-testid="licenses-tab">
            <Key className="w-4 h-4" />
            Licenses ({licenses.length})
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2" data-testid="ai-tab">
            <Zap className="w-4 h-4" />
            AI/ML ({aiServices.length})
          </TabsTrigger>
          <TabsTrigger value="renewals" className="gap-2" data-testid="renewals-tab">
            <Calendar className="w-4 h-4" />
            Renewals ({upcomingRenewals.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Spend Distribution Chart */}
            <Card data-testid="spend-distribution-chart">
              <CardHeader>
                <CardTitle className="text-lg">Spend Distribution</CardTitle>
                <CardDescription className="text-slate-400">Technology spending by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <RechartsPie>
                      <Pie
                        data={categoryChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {categoryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Optimization Opportunities */}
            <Card data-testid="optimizations-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Optimization Opportunities
                </CardTitle>
                <CardDescription className="text-slate-400">Potential savings from unused resources</CardDescription>
              </CardHeader>
              <CardContent>
                {optimizations.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No optimization opportunities found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {optimizations.map((opt, idx) => (
                      <div key={idx} className="p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{opt.service}</p>
                            <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">{opt.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(opt.monthly_savings)}/mo
                            </p>
                            <p className="text-xs text-slate-400">{formatCurrency(opt.annual_savings)}/yr</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-900 dark:text-white">Total Potential Savings</span>
                        <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(optimizations.reduce((sum, o) => sum + o.monthly_savings, 0))}/mo
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cloud Provider Breakdown */}
          <Card data-testid="cloud-provider-breakdown">
            <CardHeader>
              <CardTitle className="text-lg">Cloud Provider Breakdown</CardTitle>
              <CardDescription className="text-slate-400">Infrastructure spend by provider</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                  <BarChart data={Object.entries(cloudDetails.by_provider || {}).map(([name, value]) => ({ name, value: Math.round(value) }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SaaS Tab */}
        <TabsContent value="saas" className="space-y-6">
          <Card data-testid="saas-utilization">
            <CardHeader>
              <CardTitle className="text-lg">SaaS License Utilization</CardTitle>
              <CardDescription className="text-slate-400">
                {saasDetails.used_seats || 0} of {saasDetails.total_seats || 0} seats in use ({kpis.saas_utilization || 0}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={kpis.saas_utilization || 0} className="h-3 mb-4" />
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Wasted spend on unused seats: <span className="font-bold">{formatCurrency(saasDetails.wasted_spend || 0)}/month</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">SaaS Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 font-semibold">Application</th>
                      <th className="text-left py-3 px-4 font-semibold">Category</th>
                      <th className="text-right py-3 px-4 font-semibold">Monthly Cost</th>
                      <th className="text-center py-3 px-4 font-semibold">Seats Used</th>
                      <th className="text-center py-3 px-4 font-semibold">Utilization</th>
                      <th className="text-left py-3 px-4 font-semibold">Renewal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saasSubscriptions.map((sub, idx) => {
                      const utilization = sub.seats_total > 0 ? Math.round((sub.seats_used / sub.seats_total) * 100) : 0;
                      return (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800">
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-900 dark:text-white">{sub.name}</div>
                            <div className="text-xs text-slate-400">{sub.vendor}</div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">{sub.category}</Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">{formatCurrency(sub.monthly_cost)}</td>
                          <td className="py-3 px-4 text-center">
                            {sub.seats_used} / {sub.seats_total}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Progress value={utilization} className="h-2 w-16" />
                              <span className={`text-xs font-medium ${utilization < 50 ? 'text-rose-600' : utilization < 80 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {utilization}%
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-400">
                            {formatDate(sub.renewal_date)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Licenses Tab */}
        <TabsContent value="licenses" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Software Licenses</CardTitle>
              <CardDescription className="text-slate-400">Enterprise software and perpetual licenses</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 font-semibold">Software</th>
                      <th className="text-left py-3 px-4 font-semibold">Vendor</th>
                      <th className="text-left py-3 px-4 font-semibold">Type</th>
                      <th className="text-right py-3 px-4 font-semibold">Monthly Cost</th>
                      <th className="text-center py-3 px-4 font-semibold">Quantity</th>
                      <th className="text-left py-3 px-4 font-semibold">Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.map((license, idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <td className="py-3 px-4 font-medium text-slate-900 dark:text-white">{license.name}</td>
                        <td className="py-3 px-4 text-slate-400 dark:text-slate-400">{license.vendor}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={license.license_type === 'Subscription' ? 'border-sky-500 text-emerald-700' : 'border-amber-500 text-amber-700'}>
                            {license.license_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">{formatCurrency(license.monthly_cost)}</td>
                        <td className="py-3 px-4 text-center">{license.quantity}</td>
                        <td className="py-3 px-4 text-sm text-slate-400">{formatDate(license.expiry_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI/ML Tab */}
        <TabsContent value="ai" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-rose-500" />
                AI/ML Services
              </CardTitle>
              <CardDescription className="text-slate-400">
                Total AI spend: {formatCurrency(aiDetails.total || 0)}/month | Trend: {aiDetails.trend >= 0 ? '+' : ''}{aiDetails.trend}%
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {aiServices.map((service, idx) => (
                  <div key={idx} className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{service.service}</p>
                        <Badge variant="outline" className="mt-1 text-xs">{service.category}</Badge>
                      </div>
                      {service.trend >= 0 ? (
                        <div className="flex items-center text-rose-600 text-sm">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          +{service.trend}%
                        </div>
                      ) : (
                        <div className="flex items-center text-emerald-600 text-sm">
                          <TrendingDown className="w-4 h-4 mr-1" />
                          {service.trend}%
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(service.monthly_cost)}</p>
                      <p className="text-sm text-slate-400 mt-1">Usage: {service.usage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Renewals Tab */}
        <TabsContent value="renewals" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                Upcoming Renewals (Next 90 Days)
              </CardTitle>
              <CardDescription className="text-slate-400">Contract renewals and expiration dates</CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingRenewals.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No upcoming renewals in the next 90 days</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingRenewals.map((renewal, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-100">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${renewal.type === 'SaaS' ? 'bg-emerald-100 dark:bg-cyan-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                          {renewal.type === 'SaaS' ? (
                            <Package className={`w-5 h-5 text-emerald-600 dark:text-cyan-400`} />
                          ) : (
                            <Key className={`w-5 h-5 text-amber-600 dark:text-amber-400`} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white dark:text-white">{renewal.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{renewal.type}</Badge>
                            <span className="text-sm text-slate-400">
                              Renews: <span className="font-medium text-amber-600">{formatDate(renewal.renewal_date)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white dark:text-white">{formatCurrency(renewal.monthly_cost)}/mo</p>
                        <p className="text-sm text-slate-400">Annual: {formatCurrency(renewal.annual_value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vendor Consolidation */}
          {vendorData?.consolidation_opportunities && vendorData.consolidation_opportunities.length > 0 && (
            <Card data-testid="vendor-consolidation">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="w-5 h-5 text-emerald-500" />
                  Vendor Consolidation Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {vendorData.consolidation_opportunities.map((opp, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{opp.recommendation}</p>
                          <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">{opp.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                            Save {formatCurrency(opp.potential_savings)}/mo
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </div>
  );
};

export default TechSpend;
